import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SignpostIcon from '@mui/icons-material/Signpost';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const InfoComponent = ({ open, setOpen }) => {
  const steps = [
    {
      title: 'Frame Capture',
      icon: <VideocamIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      items: [
        'Play the video',
        'Pause the video at the desired frame',
        'Click on Capture Frame button',
        'Verify the captured frame displayed below the video',
        'If satisfied, click Next to proceed',
      ],
      color: '#e3f2fd',
    },
    {
      title: 'Location Selection',
      icon: <LocationOnIcon sx={{ fontSize: 40, color: '#d32f2f' }} />,
      items: [
        'On the map, a marker has been already placed at the exact location of the frame captured',
        'The marker position can be adjusted by dragging if required',
        'Once the sign position is accurately marked, click Next to continue',
      ],
      color: '#ffebee',
    },
    {
      title: 'Sign Selection',
      icon: <SignpostIcon sx={{ fontSize: 40, color: '#388e3c' }} />,
      items: [
        'Browse through the list of signs presented',
        'Select the sign that matches the one in your captured frame',
        'After selecting the appropriate sign, click Finish to complete the process',
      ],
      color: '#e8f5e9',
    },
  ];

  const currentStep = steps[open.activeStep] || steps[0];

  return (
    <Dialog
      open={open.open}
      slots={{
        transition: Transition,
      }}
      keepMounted
      onClose={() => setOpen({ open: false, activeStep: null })}
      aria-describedby="info-dialog-description"
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } } }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${currentStep.color} 0%, #ffffff 100%)`,
          mb: 2,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            {currentStep.icon}
            <Typography variant="h5" fontWeight={600} color="text.primary">
              {currentStep.title}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setOpen({ open: false, activeStep: null })}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box>
          {currentStep.items.map((step, index) => (
            <Box
              key={index}
              display="flex"
              alignItems="flex-start"
              gap={2}
              mb={2}
              sx={{
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Box
                sx={{
                  minWidth: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${currentStep.color} 0%, #ffffff 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: 'text.primary',
                  border: '2px solid',
                  borderColor: currentStep.icon.props.sx.color,
                }}
              >
                {index + 1}
              </Box>
              <Typography variant="body1" sx={{ pt: 0.3, lineHeight: 1.6 }}>
                {step}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default InfoComponent;

InfoComponent.propTypes = {
  setOpen: PropTypes.func.isRequired,
  open: PropTypes.object.isRequired,
};
